import React, { useState } from "react";
import { format, addDays, parseISO, isBefore } from "date-fns";
import { FiCalendar, FiClock, FiAlertTriangle, FiHome } from "react-icons/fi";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const SafeDaysCalculator = () => {
  const [lastPeriodDate, setLastPeriodDate] = useState("");
  const [minCycleLength, setMinCycleLength] = useState(26);
  const [maxCycleLength, setMaxCycleLength] = useState(32);
  const [periodLength, setPeriodLength] = useState(5);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);

  const calculateSafeDays = (e) => {
    e.preventDefault();
    setIsCalculating(true);
    setError("");

    if (!lastPeriodDate) {
      setError("Please select the first day of your last period");
      setIsCalculating(false);
      return;
    }

    const min = parseInt(minCycleLength);
    const max = parseInt(maxCycleLength);
    const periodLen = parseInt(periodLength);

    if (min < 21 || max > 45) {
      setError("Cycle length should be between 21 and 45 days");
      setIsCalculating(false);
      return;
    }

    if (min > max) {
      setError("Minimum cycle length cannot be greater than maximum");
      setIsCalculating(false);
      return;
    }

    if (periodLen < 2 || periodLen > 10) {
      setError("Period length should be between 2 and 10 days");
      setIsCalculating(false);
      return;
    }

    // Calculations
    const fertileStartOffset = min - 18;
    const fertileEndOffset = max - 11;

    const startDate = parseISO(lastPeriodDate);
    const fertileStart = addDays(startDate, fertileStartOffset);
    const fertileEnd = addDays(startDate, fertileEndOffset);

    const nextPeriodDate = addDays(startDate, min);
    const nextPeriodEndDate = addDays(nextPeriodDate, periodLen - 1);

    // Calculate period dates
    const periodDates = Array.from({ length: periodLen }, (_, i) =>
      addDays(startDate, i)
    );
    const nextCyclePeriodDates = Array.from({ length: periodLen }, (_, i) =>
      addDays(nextPeriodDate, i)
    );

    // Safe days are before and after the fertile window
    const preFertileSafeStart = startDate;
    const preFertileSafeEnd = addDays(startDate, fertileStartOffset - 1);

    const postFertileSafeStart = addDays(startDate, fertileEndOffset + 1);
    const postFertileSafeEnd = addDays(startDate, min - 1); // Until the start of the next cycle

    // Next cycle safe days
    const nextPreFertileSafeStart = nextPeriodDate;
    const nextPreFertileSafeEnd = addDays(
      nextPeriodDate,
      fertileStartOffset - 1
    );

    const nextPostFertileSafeStart = addDays(
      nextPeriodDate,
      fertileEndOffset + 1
    );
    const nextPostFertileSafeEnd = addDays(nextPeriodDate, max - 1);

    // Check if period dates fall within pre-fertile safe days
    const currentPeriodInSafeDays = periodDates.some(
      (date) => isBefore(date, fertileStart) && !isBefore(date, startDate)
    );

    const nextPeriodInSafeDays = nextCyclePeriodDates.some(
      (date) =>
        isBefore(date, addDays(nextPeriodDate, fertileStartOffset)) &&
        !isBefore(date, nextPeriodDate)
    );

    setResults({
      preFertileSafePeriod: {
        start: preFertileSafeStart,
        end: preFertileSafeEnd,
        hasPeriod: currentPeriodInSafeDays,
        periodDates: periodDates,
      },
      postFertileSafePeriod: {
        start: postFertileSafeStart,
        end: postFertileSafeEnd,
      },
      nextPreFertileSafePeriod: {
        start: nextPreFertileSafeStart,
        end: nextPreFertileSafeEnd,
        hasPeriod: nextPeriodInSafeDays,
        periodDates: nextCyclePeriodDates,
      },
      nextPostFertileSafePeriod: {
        start: nextPostFertileSafeStart,
        end: nextPostFertileSafeEnd,
      },
      fertileWindow: { start: fertileStart, end: fertileEnd },
      nextPeriod: { start: nextPeriodDate, end: nextPeriodEndDate },
    });

    setIsCalculating(false);
  };

  const formatDateLong = (date) => {
    return format(date, "EEEE, do MMMM, yyyy");
  };

  const formatDateShort = (date) => {
    return format(date, "MMM d");
  };

  return (
    <div className="w-full font-nunito min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8">
      <Helmet>
        {/* Google Analytics */}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
        ></script>
        <script>
          {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
        </script>
        <title>
          Safe Days Calculator - FlowHearts | Fertility & Period Tracker
        </title>
        <meta
          name="description"
          content="Use FlowHearts' Safe Days Calculator to track your menstrual cycle, identify safe days for contraception, and monitor fertile windows accurately."
        />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href="https://flowhearts.com/safe-days-calculator"
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://flowhearts.com/safe-days-calculator"
        />
        <meta
          property="og:title"
          content="Safe Days Calculator - FlowHearts | Fertility & Period Tracker"
        />
        <meta
          property="og:description"
          content="Track your menstrual cycle and identify safe days for contraception with FlowHearts' Safe Days Calculator, designed for fertility awareness."
        />
        <meta
          property="og:image"
          content="https://flowhearts.com/images/safe-days-calculator-preview.jpg"
        />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta
          property="twitter:url"
          content="https://flowhearts.com/safe-days-calculator"
        />
        <meta
          property="twitter:title"
          content="Safe Days Calculator - FlowHearts | Fertility & Period Tracker"
        />
        <meta
          property="twitter:description"
          content="Discover safe days and fertile windows with FlowHearts' Safe Days Calculator, a tool for menstrual cycle tracking and natural family planning."
        />
        <meta
          property="twitter:image"
          content="https://flowhearts.com/images/safe-days-calculator-preview.jpg"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Safe Days Calculator",
          "url": "https://flowhearts.com/safe-days-calculator",
          "description": "Track menstrual cycles and identify safe days for contraception with FlowHearts' Safe Days Calculator for fertility awareness.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
        </script>
      </Helmet>
      <div className="max-w-8xl mx-auto px-4 md:px-6">
        {/* Back to Home Button */}
        <div className="flex justify-start  mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
          >
            <FiHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>

        <form onSubmit={calculateSafeDays}>
          <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-3xl p-8 shadow-xl">
            <h2 className="text-4xl font-cormorant font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
              Safe Days Calculator
            </h2>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-lg font-medium mb-2 text-purple-700">
                    <FiCalendar className="inline mr-2" />
                    Last Period Start Date
                  </label>
                  <input
                    type="date"
                    value={lastPeriodDate}
                    onChange={(e) => setLastPeriodDate(e.target.value)}
                    className="w-full p-3 rounded-xl border-2 border-purple-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200"
                    required
                    max={format(new Date(), "yyyy-MM-dd")}
                  />
                </div>
                <div>
                  <label className="block text-lg font-medium mb-2 text-purple-700">
                    <FiClock className="inline mr-2" />
                    Min Cycle (days)
                  </label>
                  <input
                    type="number"
                    min="21"
                    max="45"
                    value={minCycleLength}
                    onChange={(e) => setMinCycleLength(e.target.value)}
                    className="w-full p-3 rounded-xl border-2 border-purple-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-lg font-medium mb-2 text-purple-700">
                    <FiClock className="inline mr-2" />
                    Max Cycle (days)
                  </label>
                  <input
                    type="number"
                    min="21"
                    max="45"
                    value={maxCycleLength}
                    onChange={(e) => setMaxCycleLength(e.target.value)}
                    className="w-full p-3 rounded-xl border-2 border-purple-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200"
                    required
                  />
                </div>
              </div>

              <div className="w-full">
                <label className="block text-lg font-medium mb-2 text-purple-700">
                  <FiClock className="inline mr-2" />
                  Period Length (days)
                </label>
                <input
                  type="number"
                  min="2"
                  max="10"
                  value={periodLength}
                  onChange={(e) => setPeriodLength(e.target.value)}
                  className="w-full p-3 rounded-xl border-2 border-purple-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-xl">
                  <p>{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isCalculating}
                className="w-full py-3 px-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl shadow-md hover:from-pink-600 hover:to-purple-700 transition-all duration-300"
              >
                {isCalculating ? "Calculating..." : "Calculate Safe Days"}
              </button>
            </div>

            {results && (
              <div className="mt-8 space-y-6 w-full">
                {/* Safe Periods - Tab-like design */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 shadow-lg">
                  <h3 className="text-2xl font-semibold mb-6 text-center text-purple-700">
                    Your Safe Periods
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-4 rounded-xl shadow-sm">
                      <h4 className="font-bold text-lg mb-3 text-green-600">
                        Current Cycle Safe Days
                      </h4>
                      <div className="space-y-3">
                        <div className="bg-green-100 p-3 rounded-lg">
                          <p className="font-medium">Pre-Fertile Safe Days:</p>
                          <p className="text-green-800">
                            {formatDateLong(results.preFertileSafePeriod.start)}{" "}
                            to{" "}
                            {formatDateLong(results.preFertileSafePeriod.end)}
                          </p>
                          {results.preFertileSafePeriod.hasPeriod && (
                            <div className="mt-2 bg-red-50 border-l-4 border-red-400 p-2 rounded-xl flex items-start">
                              <FiAlertTriangle className="text-red-500 mt-1 mr-2 flex-shrink-0" />
                              <div>
                                <p className="text-red-700 font-medium">
                                  Warning:
                                </p>
                                <p className="text-red-600 text-sm">
                                  You might have period flow during these days
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="bg-green-100 p-3 rounded-lg">
                          <p className="font-medium">Post-Fertile Safe Days:</p>
                          <p className="text-green-800">
                            {formatDateLong(
                              results.postFertileSafePeriod.start
                            )}{" "}
                            to{" "}
                            {formatDateLong(results.postFertileSafePeriod.end)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm">
                      <h4 className="font-bold text-lg mb-3 text-blue-600">
                        Next Cycle Safe Days
                      </h4>
                      <div className="space-y-3">
                        <div className="bg-blue-100 p-3 rounded-lg">
                          <p className="font-medium">Pre-Fertile Safe Days:</p>
                          <p className="text-blue-800">
                            {formatDateLong(
                              results.nextPreFertileSafePeriod.start
                            )}{" "}
                            to{" "}
                            {formatDateLong(
                              results.nextPreFertileSafePeriod.end
                            )}
                          </p>
                          {results.nextPreFertileSafePeriod.hasPeriod && (
                            <div className="mt-2 bg-red-50 border-l-4 border-red-400 p-2 rounded-xl flex items-start">
                              <FiAlertTriangle className="text-red-500 mt-1 mr-2 flex-shrink-0" />
                              <div>
                                <p className="text-red-700 font-medium">
                                  Warning:
                                </p>
                                <p className="text-red-600 text-sm">
                                  You might have period flow during these days
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="bg-blue-100 p-3 rounded-lg">
                          <p className="font-medium">Post-Fertile Safe Days:</p>
                          <p className="text-blue-800">
                            {formatDateLong(
                              results.nextPostFertileSafePeriod.start
                            )}{" "}
                            to{" "}
                            {formatDateLong(
                              results.nextPostFertileSafePeriod.end
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Important Dates - Tab-like design */}
                <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl p-6 shadow-lg">
                  <h3 className="text-2xl font-semibold mb-6 text-center text-purple-700">
                    Important Dates
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-4 rounded-xl shadow-sm">
                      <h4 className="font-bold text-lg mb-3 text-red-600">
                        Fertile Window
                      </h4>
                      <div className="bg-red-100 p-3 rounded-lg">
                        <p className="text-red-800">
                          {formatDateLong(results.fertileWindow.start)} to{" "}
                          {formatDateLong(results.fertileWindow.end)}
                        </p>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm">
                      <h4 className="font-bold text-lg mb-3 text-pink-600">
                        Next Period
                      </h4>
                      <div className="bg-pink-100 p-3 rounded-lg">
                        <p className="text-pink-800">
                          {formatDateLong(results.nextPeriod.start)} to{" "}
                          {formatDateLong(results.nextPeriod.end)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Color Legend */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-center text-purple-700">
                    Color Guide
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center justify-center bg-green-100 p-3 rounded-lg">
                      <span className="w-4 h-4 bg-green-500 rounded-full mr-2"></span>
                      <span className="font-medium">Safe Days</span>
                    </div>
                    <div className="flex items-center justify-center bg-red-100 p-3 rounded-lg">
                      <span className="w-4 h-4 bg-red-500 rounded-full mr-2"></span>
                      <span className="font-medium">Fertile Window</span>
                    </div>
                    <div className="flex items-center justify-center bg-pink-100 p-3 rounded-lg">
                      <span className="w-4 h-4 bg-pink-500 rounded-full mr-2"></span>
                      <span className="font-medium">Period Days</span>
                    </div>
                  </div>
                </div>

                {/* Educational Note */}
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-xl">
                  <p className="text-yellow-700">
                    <span className="font-medium">ℹ️ Note:</span> This is based
                    on the calendar method. It does not account for irregular
                    cycles, illness, stress, or hormone changes. For more
                    accurate contraception, consider consulting a healthcare
                    provider.
                  </p>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
      {/* Educational Section */}
      <div className="max-w-8xl mx-auto px-4 md:px-6 mt-12">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* How to Use Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiCalendar className="mr-3 text-pink-500" />
              How to Use the Safe Days Calculator
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Enter Your Cycle Data
                </h3>
                <p className="text-gray-700 text-center">
                  Input your last period date, cycle length range, and period
                  duration
                </p>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold text-center text-purple-700 mb-2">
                  Calculate Safe Days
                </h3>
                <p className="text-gray-700 text-center">
                  The calculator will determine your fertile window and safe
                  periods
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold text-center text-green-700 mb-2">
                  Understand Results
                </h3>
                <p className="text-gray-700 text-center">
                  View color-coded safe days and fertility periods in your cycle
                </p>
              </div>
            </div>
          </div>

          {/* The Science Behind */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiClock className="mr-3 text-purple-500" />
              The Science Behind Safe Days Calculation
            </h2>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mb-6">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                Calendar Method Formula
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-pink-600 mb-2">
                    Fertile Window Start
                  </p>
                  <code className="block bg-gray-50 p-2 rounded text-purple-700 font-mono">
                    Fertile Start = Last Period + (Shortest Cycle - 18)
                  </code>
                </div>

                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-purple-600 mb-2">
                    Fertile Window End
                  </p>
                  <code className="block bg-gray-50 p-2 rounded text-purple-700 font-mono">
                    Fertile End = Last Period + (Longest Cycle - 11)
                  </code>
                </div>
              </div>

              <p className="text-gray-700">
                This method is based on the{" "}
                <span className="font-semibold">Standard Days Method</span>
                which assumes sperm can survive up to 5 days and the egg is
                viable for about 24 hours after ovulation. The calculations
                account for these biological factors.
              </p>
            </div>
          </div>

          {/* Biological Basis */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiAlertTriangle className="mr-3 text-blue-500" />
              Understanding Fertility Biology
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">
                  Fertility Factors
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <FiClock className="text-blue-500 mr-2 mt-0.5" />
                    <span>
                      Sperm can survive 3-5 days in reproductive tract
                    </span>
                  </li>
                  <li className="flex items-start">
                    <FiClock className="text-blue-500 mr-2 mt-0.5" />
                    <span>Egg is viable for 12-24 hours after ovulation</span>
                  </li>
                  <li className="flex items-start">
                    <FiClock className="text-blue-500 mr-2 mt-0.5" />
                    <span>
                      Ovulation typically occurs 14 days before next period
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-3">
                  Cycle Variability
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <FiAlertTriangle className="text-pink-500 mr-2 mt-0.5" />
                    <span>Stress can delay ovulation by several days</span>
                  </li>
                  <li className="flex items-start">
                    <FiAlertTriangle className="text-pink-500 mr-2 mt-0.5" />
                    <span>Illness may affect cycle regularity</span>
                  </li>
                  <li className="flex items-start">
                    <FiAlertTriangle className="text-pink-500 mr-2 mt-0.5" />
                    <span>Hormonal changes can shift fertile window</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Accuracy Note */}
          <div className="mb-12 bg-white rounded-xl p-6 shadow-inner border border-pink-300">
            <h3 className="text-xl font-semibold text-pink-800 mb-4 flex items-center">
              <FiAlertTriangle className="mr-2 text-pink-500" />
              Important Note on Accuracy
            </h3>
            <p className="text-gray-700 mb-3">
              The calendar method has significant limitations:
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <FiAlertTriangle className="text-pink-500 mr-2 mt-0.5" />
                <span>Only about 76-88% effective with perfect use</span>
              </li>
              <li className="flex items-start">
                <FiAlertTriangle className="text-pink-500 mr-2 mt-0.5" />
                <span>Requires regular, predictable cycles</span>
              </li>
              <li className="flex items-start">
                <FiAlertTriangle className="text-pink-500 mr-2 mt-0.5" />
                <span>Doesn't account for cycle irregularities</span>
              </li>
            </ul>
            <p className="text-gray-700">
              For reliable contraception, combine with other methods like basal
              body temperature tracking or ovulation tests, and consult a
              healthcare provider.
            </p>
          </div>

          {/* New Section: Benefits of Fertility Awareness */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiCalendar className="mr-3 text-green-500" />
              Benefits of Fertility Awareness Methods
            </h2>
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
              <p className="text-gray-700 mb-4">
                Embracing fertility awareness through menstrual cycle tracking
                can empower women in their reproductive health journey. This
                natural approach to family planning helps in understanding body
                signals and making informed decisions.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FiAlertTriangle className="text-green-500 mr-2 mt-0.5" />
                  <span>
                    Promotes hormone-free contraception options for those
                    seeking natural methods.
                  </span>
                </li>
                <li className="flex items-start">
                  <FiAlertTriangle className="text-green-500 mr-2 mt-0.5" />
                  <span>
                    Enhances overall women's health awareness by spotting
                    patterns in cycle changes.
                  </span>
                </li>
                <li className="flex items-start">
                  <FiAlertTriangle className="text-green-500 mr-2 mt-0.5" />
                  <span>
                    Can aid in planning pregnancy when used in reverse for
                    optimal conception timing.
                  </span>
                </li>
                <li className="flex items-start">
                  <FiAlertTriangle className="text-green-500 mr-2 mt-0.5" />
                  <span>
                    Supports mental well-being by reducing anxiety around
                    unexpected cycle variations.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* New Section: Common Myths Debunked */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiAlertTriangle className="mr-3 text-red-500" />
              Debunking Common Myths About Safe Days
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Safe Days Are 100% Reliable
                </h3>
                <p className="text-gray-700">
                  Reality: While helpful for menstrual cycle tracking, factors
                  like stress or diet can alter ovulation, making natural family
                  planning less predictable. Always use backup methods for
                  contraception.
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Only for Regular Cycles
                </h3>
                <p className="text-gray-700">
                  Reality: Even with varying cycles, tools like this period
                  tracker can provide insights. Combining with symptoms like
                  cervical mucus monitoring improves accuracy in fertility
                  awareness.
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Safe During Periods
                </h3>
                <p className="text-gray-700">
                  Reality: Pregnancy risk is low but possible if cycles are
                  short and ovulation occurs early. Understanding your unique
                  women's health patterns is key.
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Apps Replace Medical Advice
                </h3>
                <p className="text-gray-700">
                  Reality: Digital tools enhance natural family planning but
                  consulting professionals ensures personalized guidance for
                  reproductive health.
                </p>
              </div>
            </div>
          </div>

          {/* New Section: Tips for Better Tracking */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiClock className="mr-3 text-blue-500" />
              Practical Tips for Effective Cycle Tracking
            </h2>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
              <p className="text-gray-700 mb-4">
                Elevate your fertility awareness method with these actionable
                strategies to make menstrual cycle tracking a seamless part of
                your routine.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">
                    Track Consistently
                  </h3>
                  <p className="text-gray-700 text-sm">
                    Log daily symptoms in a period tracker app to spot trends in
                    your cycle.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">
                    Monitor Body Signs
                  </h3>
                  <p className="text-gray-700 text-sm">
                    Note changes in basal temperature and cervical position for
                    better women's health insights.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">
                    Lifestyle Integration
                  </h3>
                  <p className="text-gray-700 text-sm">
                    Consider how exercise and nutrition impact your natural
                    family planning efforts.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">
                    Regular Check-ins
                  </h3>
                  <p className="text-gray-700 text-sm">
                    Review your data monthly to adjust for any reproductive
                    health shifts.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* New Section: When to Seek Professional Help */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FiAlertTriangle className="mr-3 text-yellow-500" />
              When to Consult a Healthcare Professional
            </h2>
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
              <p className="text-gray-700 mb-4">
                While self-tracking is empowering, certain signs in your
                menstrual cycle may warrant expert advice for optimal women's
                health.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FiAlertTriangle className="text-yellow-500 mr-2 mt-0.5" />
                  <span>
                    If cycles become highly irregular despite consistent
                    tracking.
                  </span>
                </li>
                <li className="flex items-start">
                  <FiAlertTriangle className="text-yellow-500 mr-2 mt-0.5" />
                  <span>
                    Experiencing unusual pain or heavy bleeding during periods.
                  </span>
                </li>
                <li className="flex items-start">
                  <FiAlertTriangle className="text-yellow-500 mr-2 mt-0.5" />
                  <span>
                    Planning pregnancy but facing challenges with conception
                    timing.
                  </span>
                </li>
                <li className="flex items-start">
                  <FiAlertTriangle className="text-yellow-500 mr-2 mt-0.5" />
                  <span>
                    After major life changes affecting your fertility awareness.
                  </span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                Remember, tools like this safe days calculator complement
                professional guidance in natural family planning.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafeDaysCalculator;
